// src/utils/eventFabPrompts.js
export function buildEventFabSystemPrompt() {
  return `
You are “EventFab Planner”, an expert event producer + fabrication PM + estimator.

GOALS
- Plan and fabricate an event: products, pricing, turnaround, logistics, labor, and contingency based on a target budget.
- If critical details are missing, use reasonable assumptions and label them.

RULES
- The user requires "reasoning" FIRST, but make it client-facing (no hidden chain-of-thought).
- Output MUST be a single JSON object with fields in this exact order:
  1) "reasoning"
  2) "event_plan" { "products": [...], "cost_breakdown": {...}, "timeline": [...], "logistics_notes": "...", "budget_suggestions": [...] }
  3) "conclusion"
- Use numeric prices only (no $). Use placeholders like "TBD_VENDOR_1" when uncertain.
- Include fabrication, labor, rentals/purchases, logistics, fees, contingency.
- Include tradeoffs/substitutions for cost control or enhancements if under budget.
- Ensure totals add up and align to budget target.

NEVER wrap the JSON in markdown. NEVER add extra keys.`;
}

export function buildEventFabUserPayload({ event, products, costs, totals, research, uploads }) {
  const key_needs = (event.key_needs_text || "").split(",").map(x => x.trim()).filter(Boolean);

  // keep sources compact
  const sources = (research?.results || []).slice(0, 12).map(r => ({
    title: r.title, link: r.link, host: r.host, snippet: r.snippet
  }));

  const payload = {
    event_type: event.event_type || "TBD",
    budget: Number(event.budget || 0),
    key_needs,
    location: event.location || "TBD",
    date: event.date || "TBD",
    attendees: event.attendees || "TBD",
    constraints: event.constraints || "",
    look_and_feel: event.look_and_feel || "",
    uploads: (uploads || []).slice(0, 6).map(u => ({ name: u.name, type: u.type })),
    existing_products: (products || []).map(p => ({
      product_name: p.product_name,
      purpose: p.purpose,
      qty: Number(p.qty || 1),
      unit_price: Number(p.unit_price || 0),
      turnaround_time: p.turnaround_time,
      supplier_options: p.supplier_options || [],
      notes: p.notes || ""
    })),
    cost_inputs: {
      labor: Number(costs.labor || 0),
      logistics: Number(costs.logistics || 0),
      fees: Number(costs.fees || 0),
      contingencyPct: Number(costs.contingencyPct || 0)
    },
    totals,
    research: {
      query: research?.query || "",
      summary: research?.summary || "",
      sources
    }
  };

  // IMPORTANT: request the exact JSON structure
  return `
Create the plan using this input. Preserve budget and priorities. If something is unknown, use placeholders.

INPUT:
${JSON.stringify(payload, null, 2)}

OUTPUT FORMAT:
{
  "reasoning": "...",
  "event_plan": {
    "products": [
      { "product_name": "...", "purpose": "...", "price_estimate": 0, "turnaround_time": "...", "supplier_options": ["..."] }
    ],
    "cost_breakdown": {
      "items_and_services": { },
      "totals": { "subtotal": 0, "total": 0 }
    },
    "timeline": ["..."],
    "logistics_notes": "...",
    "budget_suggestions": ["..."]
  },
  "conclusion": "..."
}
`;
}
